"use client";

import React, { useMemo } from "react";
import {
  useContactTags,
  useTicketTags,
  useTaskTags,
  useCompanyTags,
  useDealTags,
  useCompanyTechnologies,
} from "@/lib/hooks/api/tags/tag-hooks";
import type { SimpleTableConfig } from "@/components/shared/UniversalCRMTable/types";
import type {
  Contacts,
  Tickets,
  Companies,
  Social_Media_Posts,
  Email_Campaigns,
} from "@/src/gql/graphql";
import { useMarketingBrands } from "@/lib/hooks/marketing/useMarketingBrands";
import type { Deal } from "@/lib/types/database/deals";
import type { ContactTaskRow } from "@/lib/types/entity-rows";

/**
 * Hook to enhance contacts config with dynamic tag options
 *
 * Fetches unique tags from all contacts and injects them as options
 * into the tags field config. This allows the SimpleTableHeaderFilterDropdowns
 * to render tags as a filterable field.
 */
export function useContactsConfigEnhancer({
  config,
}: {
  config: SimpleTableConfig<Contacts>;
}): SimpleTableConfig<Contacts> {
  const { tags } = useContactTags();

  const enhancedConfig = useMemo(() => {
    if (!config.fields?.tags) return config;

    const enhancedFields = { ...config.fields };
    enhancedFields.tags = {
      ...enhancedFields.tags,
      options: tags.map((tag: string) => ({
        label: tag,
        value: tag,
      })),
    };

    return {
      ...config,
      fields: enhancedFields,
    };
  }, [config, tags]);

  return enhancedConfig;
}

/**
 * Hook to enhance tickets config with dynamic tag options
 */
export function useTicketsConfigEnhancer({
  config,
}: {
  config: SimpleTableConfig<Tickets>;
}): SimpleTableConfig<Tickets> {
  const { tags } = useTicketTags();

  const enhancedConfig = useMemo(() => {
    if (!config.fields?.tags) return config;

    const enhancedFields = { ...config.fields };
    enhancedFields.tags = {
      ...enhancedFields.tags,
      options: tags.map((tag: string) => ({
        label: tag,
        value: tag,
      })),
    };

    return {
      ...config,
      fields: enhancedFields,
    };
  }, [config, tags]);

  return enhancedConfig;
}

/**
 * Hook to enhance tasks config with dynamic tag options
 */
export function useTasksConfigEnhancer({
  config,
}: {
  config: SimpleTableConfig<ContactTaskRow>;
}): SimpleTableConfig<ContactTaskRow> {
  const { tags } = useTaskTags();

  const enhancedConfig = useMemo(() => {
    if (!config.fields?.tags) return config;

    const enhancedFields = { ...config.fields };
    enhancedFields.tags = {
      ...enhancedFields.tags,
      options: tags.map((tag: string) => ({
        label: tag,
        value: tag,
      })),
    };

    return {
      ...config,
      fields: enhancedFields,
    };
  }, [config, tags]);

  return enhancedConfig;
}

/**
 * Hook to enhance companies config with dynamic tag options
 *
 * Fetches unique tags from all companies and injects them as options
 * into the tags field config. This allows the SimpleTableHeaderFilterDropdowns
 * to render tags as a filterable field.
 */
export function useCompaniesTagsConfigEnhancer({
  config,
}: {
  config: SimpleTableConfig<Companies>;
}): SimpleTableConfig<Companies> {
  const { tags } = useCompanyTags();

  const enhancedConfig = useMemo(() => {
    if (!config.fields?.tags) return config;

    const enhancedFields = { ...config.fields };
    enhancedFields.tags = {
      ...enhancedFields.tags,
      options: tags.map((tag: string) => ({
        label: tag,
        value: tag,
      })),
    };

    return {
      ...config,
      fields: enhancedFields,
    };
  }, [config, tags]);

  return enhancedConfig;
}

/**
 * Hook to enhance deals config with dynamic tag options
 *
 * Fetches unique tags from all deals and injects them as options
 * into the tags field config. This allows the SimpleTableHeaderFilterDropdowns
 * to render tags as a filterable field.
 */
export function useDealsTagsConfigEnhancer({
  config,
}: {
  config: SimpleTableConfig<Deal>;
}): SimpleTableConfig<Deal> {
  const { tags } = useDealTags();

  const enhancedConfig = useMemo(() => {
    if (!config.fields?.tags) return config;

    const enhancedFields = { ...config.fields };
    enhancedFields.tags = {
      ...enhancedFields.tags,
      options: tags.map((tag: string) => ({
        label: tag,
        value: tag,
      })),
    };

    return {
      ...config,
      fields: enhancedFields,
    };
  }, [config, tags]);

  return enhancedConfig;
}

/**
 * Hook to enhance companies config with dynamic technology options
 *
 * Fetches unique technologies from all companies and injects them as options
 * into the current_technologies field config. This allows the SimpleTableHeaderFilterDropdowns
 * to render technologies as a filterable field.
 */
/**
 * The only shape this enhancer reads off a company row. Kept structural (rather
 * than `Companies`) so a partially-projected list row is still accepted.
 */
interface CompanyTechnologiesRow {
  current_technologies?: unknown;
}

/**
 * One allocation for the omitted-argument case. A `= []` default literal is a
 * FRESH array on every call, which made the `useMemo` below recompute on every
 * render for every caller that doesn't pass `companiesData` (MEMO_STABLE_PROPS).
 */
const EMPTY_COMPANY_ROWS: ReadonlyArray<CompanyTechnologiesRow> = [];

export function useCompaniesConfigEnhancer({
  config,
  companiesData = EMPTY_COMPANY_ROWS,
}: {
  config: SimpleTableConfig<Companies>;
  companiesData?: ReadonlyArray<CompanyTechnologiesRow | null | undefined>;
}): SimpleTableConfig<Companies> {
  const { technologies } = useCompanyTechnologies();

  const enhancedConfig = useMemo(() => {
    if (!config.fields?.current_technologies) return config;

    // Use technologies from the hook (fetched from GraphQL)
    // Fall back to extracting from companiesData if available
    let techList = technologies;

    if (techList.length === 0 && companiesData.length > 0) {
      // Fallback: Extract technologies from the companies data
      const techSet = new Set<string>();
      companiesData.forEach((company) => {
        const technologiesMap = company?.current_technologies;
        if (technologiesMap && typeof technologiesMap === "object") {
          Object.keys(technologiesMap).forEach((tech: string) => {
            if (tech) techSet.add(tech);
          });
        }
      });
      techList = Array.from(techSet).sort();
    }

    if (techList.length === 0) {
      // No technologies found, return unchanged config
      return config;
    }

    const enhancedFields = { ...config.fields };
    enhancedFields.current_technologies = {
      ...enhancedFields.current_technologies,
      options: techList.map((tech: string) => ({
        label: tech,
        value: tech,
      })),
    };

    return {
      ...config,
      fields: enhancedFields,
    };
  }, [config, technologies, companiesData]);

  return enhancedConfig;
}

/**
 * Inject the live marketing-brand list into an entity config's `brand` field.
 *
 * WHY: brands used to be a compile-time union, so `SOCIAL_POST_BRAND_OPTIONS` /
 * `EMAIL_CAMPAIGN_BRAND_OPTIONS` could be module constants. Brands are rows in
 * `marketing_brands` now — a brand onboarded under Marketing → Brands has to
 * appear in the brand filter, the badge label and the inline editor without a
 * deploy — so the options are injected at render time, exactly like the tag
 * enhancers above.
 *
 * Shared by the social-posts and email-campaigns configs: both carry a `brand`
 * field with the same vocabulary, so one enhancer serves both.
 */
function useBrandFieldEnhancer<T extends { id: string }>(
  config: SimpleTableConfig<T>
): SimpleTableConfig<T> {
  const { options } = useMarketingBrands();

  return useMemo(() => {
    if (!config.fields?.brand) return config;

    const enhancedFields = { ...config.fields };
    enhancedFields.brand = {
      ...enhancedFields.brand,
      options: options.map((option) => ({ label: option.label, value: option.value })),
    };

    return { ...config, fields: enhancedFields };
  }, [config, options]);
}

/** Social posts: dynamic brand options. */
export function useSocialPostsConfigEnhancer({
  config,
}: {
  config: SimpleTableConfig<Social_Media_Posts>;
}): SimpleTableConfig<Social_Media_Posts> {
  return useBrandFieldEnhancer(config);
}

/** Email campaigns: dynamic brand options. */
export function useEmailCampaignsConfigEnhancer({
  config,
}: {
  config: SimpleTableConfig<Email_Campaigns>;
}): SimpleTableConfig<Email_Campaigns> {
  return useBrandFieldEnhancer(config);
}
